import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { gbp, toDisplay, toGbp, curSymbol } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, GraduationCap, Plus, Trash2, Check, RotateCcw, CalendarClock, Wallet, Loader2, Pencil } from "lucide-react";

const CAT_COLORS = {
  Tuition: "#00C896", Books: "#38BDF8", Lunch: "#F59E0B", Transport: "#A78BFA",
  Research: "#F43F5E", Supplies: "#2DD4BF", Accommodation: "#FB923C", Other: "#64748B",
};

export default function StudentHub({ onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [budgetOpen, setBudgetOpen] = useState(false);
  const [bForm, setBForm] = useState({ term_label: "", term_budget: "" });
  const [savingB, setSavingB] = useState(false);

  const [itemOpen, setItemOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [iForm, setIForm] = useState({ title: "", category: "Books", amount: "", due_date: "", status: "planned", notes: "" });
  const [savingI, setSavingI] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/student-hub");
      setData(data);
      if (data.budget) setBForm({ term_label: data.budget.term_label, term_budget: String(toDisplay(data.budget.term_budget)) });
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveBudget = async () => {
    if (!bForm.term_label || !bForm.term_budget) return;
    setSavingB(true);
    try {
      await api.post("/student-hub/budget", { term_label: bForm.term_label, term_budget: toGbp(bForm.term_budget) });
      toast.success("Term budget saved");
      setBudgetOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingB(false);
    }
  };

  const openAddItem = () => { setEditing(null); setIForm({ title: "", category: "Books", amount: "", due_date: "", status: "planned", notes: "" }); setItemOpen(true); };
  const openEditItem = (it) => { setEditing(it); setIForm({ title: it.title, category: it.category, amount: String(toDisplay(it.amount)), due_date: it.due_date || "", status: it.status, notes: it.notes || "" }); setItemOpen(true); };

  const saveItem = async () => {
    if (!iForm.title || !iForm.amount) return;
    setSavingI(true);
    const payload = { title: iForm.title, category: iForm.category, amount: toGbp(iForm.amount), due_date: iForm.due_date || null, status: iForm.status, notes: iForm.notes || null };
    try {
      if (editing) await api.patch(`/student-hub/items/${editing.id}`, payload);
      else await api.post("/student-hub/items", payload);
      toast.success(editing ? "Expense updated" : "Expense added");
      setItemOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingI(false);
    }
  };

  const togglePaid = async (it) => {
    try {
      await api.patch(`/student-hub/items/${it.id}`, { status: it.status === "paid" ? "planned" : "paid" });
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const removeItem = async (it) => {
    try {
      await api.delete(`/student-hub/items/${it.id}`);
      toast.success("Expense removed");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (loading) {
    return <div className="h-64 flex items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#00C896]" /></div>;
  }

  const s = data.summary;
  const hasBudget = !!data.budget;
  const committedPct = hasBudget && s.term_budget ? Math.min(100, (s.committed / s.term_budget) * 100) : 0;
  const paidPct = hasBudget && s.term_budget ? Math.min(100, (s.paid / s.term_budget) * 100) : 0;
  const over = hasBudget && s.committed > s.term_budget;
  const maxCat = Math.max(1, ...s.by_category.map((c) => c.amount));

  return (
    <div className="mm-fade-up">
      <button data-testid="student-back-button" onClick={onBack} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition mb-5">
        <ArrowLeft className="h-4 w-4" /> Back to dashboard
      </button>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#00C896]">Student Hub</p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1 flex items-center gap-2.5">
            <GraduationCap className="h-8 w-8 text-[#00C896]" /> Plan your study budget
          </h1>
          <p className="text-slate-400 text-sm mt-2 max-w-xl">Keep an eye on study costs — tuition, books, lunch, travel to campus, research and more — all in one place.</p>
        </div>
        <Button data-testid="student-add-expense" onClick={openAddItem} className="rounded-full bg-[#00C896] hover:bg-[#00E5AC] text-[#04140D] font-semibold">
          <Plus className="h-4 w-4 mr-1.5" /> Add expense
        </Button>
      </div>

      <div className="mt-6 grid grid-cols-12 gap-4 sm:gap-5">
        {/* Term budget */}
        <div className="col-span-12 lg:col-span-5">
          <div className="mm-glow-card p-6 h-full" data-testid="student-budget-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="h-10 w-10 rounded-xl bg-[#00C896]/15 flex items-center justify-center text-[#00C896]"><Wallet className="h-5 w-5" /></span>
                <div>
                  <h3 className="font-display text-lg font-semibold">{hasBudget ? data.budget.term_label : "Term budget"}</h3>
                  <p className="text-xs text-slate-500">Your total allowance for this term</p>
                </div>
              </div>
              <button data-testid="student-edit-budget" onClick={() => setBudgetOpen(true)} className="text-slate-400 hover:text-white transition"><Pencil className="h-4 w-4" /></button>
            </div>

            {hasBudget ? (
              <div className="mt-5">
                <div className="flex items-end justify-between mb-1">
                  <span className="font-num text-3xl font-extrabold">{gbp(s.committed)}</span>
                  <span className="font-num text-sm text-slate-400">of {gbp(s.term_budget)}</span>
                </div>
                <div className="h-2.5 rounded-full bg-white/5 overflow-hidden mt-2 relative">
                  <div className="h-full rounded-full absolute top-0 left-0" style={{ width: `${committedPct}%`, background: over ? "#F43F5E" : "rgba(0,200,150,0.35)" }} />
                  <div className="h-full rounded-full absolute top-0 left-0" style={{ width: `${paidPct}%`, background: "#00C896" }} />
                </div>
                <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-400">
                  <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#00C896]" /> Paid</span>
                  <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#00C896]/35" /> Committed</span>
                </div>
                <p className={`mt-3 text-sm font-num ${over ? "text-rose-400" : "text-[#00C896]"}`}>
                  {over ? `${gbp(s.committed - s.term_budget)} over your term budget` : `${gbp(s.remaining)} left to allocate`}
                </p>
              </div>
            ) : (
              <div className="mt-5 rounded-xl border border-dashed border-white/12 p-6 text-center">
                <GraduationCap className="h-6 w-6 text-slate-500 mx-auto" />
                <p className="text-sm text-slate-400 mt-2">Set a term budget to track your study spending.</p>
                <Button data-testid="student-set-budget" onClick={() => setBudgetOpen(true)} variant="outline" className="mt-3 rounded-full border-[#00C896]/40 bg-transparent text-[#00C896] hover:bg-[#00C896]/10">Set term budget</Button>
              </div>
            )}

            <div className="mt-5 grid grid-cols-3 gap-2">
              {[["Committed", s.committed], ["Paid", s.paid], ["Outstanding", s.outstanding]].map(([l, v]) => (
                <div key={l} className="rounded-xl border border-white/8 bg-[#0D0D0D] p-3">
                  <p className="text-[10px] text-slate-500">{l}</p>
                  <p className="font-num text-sm font-bold mt-0.5">{gbp(v)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Category breakdown + upcoming */}
        <div className="col-span-12 lg:col-span-7 space-y-4">
          <div className="mm-glow-card p-6" data-testid="student-breakdown">
            <h3 className="font-display text-lg font-semibold">Spending by study category</h3>
            <div className="mt-4 space-y-2.5">
              {s.by_category.map((c) => (
                <div key={c.category}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-300">{c.category}</span>
                    <span className="font-num text-slate-400">{gbp(c.amount)}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${(c.amount / maxCat) * 100}%`, background: CAT_COLORS[c.category] || "#64748B" }} />
                  </div>
                </div>
              ))}
              {s.by_category.length === 0 && <p className="text-sm text-slate-500">No expenses yet. Add your first study expense to see the breakdown.</p>}
            </div>
          </div>

          <div className="mm-glow-card p-6" data-testid="student-upcoming">
            <h3 className="font-display text-lg font-semibold flex items-center gap-2"><CalendarClock className="h-5 w-5 text-slate-400" /> Due soon</h3>
            <div className="mt-3 space-y-2">
              {data.upcoming.map((it) => (
                <div key={it.id} className="flex items-center justify-between rounded-xl border border-white/8 bg-[#0D0D0D] px-3.5 py-2.5">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: CAT_COLORS[it.category] }} />
                    <div className="min-w-0">
                      <p className="text-sm truncate">{it.title}</p>
                      <p className="text-[11px] text-slate-500">{it.category} · due {it.due_date}</p>
                    </div>
                  </div>
                  <span className={`text-[11px] font-num shrink-0 ${it.days_left <= 3 ? "text-rose-400" : "text-amber-300"}`}>{it.days_left}d</span>
                </div>
              ))}
              {data.upcoming.length === 0 && <p className="text-sm text-slate-500">Nothing due in the next 30 days.</p>}
            </div>
          </div>
        </div>

        {/* Expense list */}
        <div className="col-span-12">
          <div className="mm-glow-card p-6" data-testid="student-expense-list">
            <h3 className="font-display text-lg font-semibold">Study expenses</h3>
            <div className="mt-4 divide-y divide-white/6">
              {data.items.map((it) => (
                <div key={it.id} data-testid={`student-item-${it.id}`} className="flex items-center justify-between gap-3 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${CAT_COLORS[it.category]}22` }}>
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: CAT_COLORS[it.category] }} />
                    </span>
                    <div className="min-w-0">
                      <p className={`text-sm font-medium truncate ${it.status === "paid" ? "line-through text-slate-500" : ""}`}>{it.title}</p>
                      <p className="text-[11px] text-slate-500">{it.category}{it.due_date ? ` · due ${it.due_date}` : ""}{it.notes ? ` · ${it.notes}` : ""}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="font-num text-sm font-semibold">{gbp(it.amount)}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${it.status === "paid" ? "border-[#00C896]/30 bg-[#00C896]/10 text-[#00C896]" : "border-amber-500/30 bg-amber-500/10 text-amber-300"}`}>{it.status}</span>
                    <button data-testid={`student-toggle-${it.id}`} onClick={() => togglePaid(it)} className="text-slate-500 hover:text-[#00C896] transition" title={it.status === "paid" ? "Mark planned" : "Mark paid"}>
                      {it.status === "paid" ? <RotateCcw className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                    </button>
                    <button data-testid={`student-edit-${it.id}`} onClick={() => openEditItem(it)} className="text-slate-500 hover:text-white transition"><Pencil className="h-3.5 w-3.5" /></button>
                    <button data-testid={`student-delete-${it.id}`} onClick={() => removeItem(it)} className="text-slate-500 hover:text-rose-400 transition"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
              {data.items.length === 0 && <p className="text-sm text-slate-500 py-6 text-center">No study expenses yet. Tap "Add expense" to plan your first one.</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Budget modal */}
      <Dialog open={budgetOpen} onOpenChange={setBudgetOpen}>
        <DialogContent className="bg-[#14181D] border-white/10 text-white" data-testid="student-budget-modal">
          <DialogHeader><DialogTitle className="font-display">Set term budget</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-slate-300 text-sm">Term name</Label>
              <Input data-testid="term-label-input" value={bForm.term_label} onChange={(e) => setBForm({ ...bForm, term_label: e.target.value })} placeholder="Autumn Term 2026" className="mt-1.5 bg-[#0D0D0D] border-white/10 text-white focus-visible:ring-[#00C896]" />
            </div>
            <div>
              <Label className="text-slate-300 text-sm">Total budget ({curSymbol()})</Label>
              <Input data-testid="term-budget-input" type="number" min="1" step="1" value={bForm.term_budget} onChange={(e) => setBForm({ ...bForm, term_budget: e.target.value })} placeholder="3000" className="mt-1.5 bg-[#0D0D0D] border-white/10 text-white focus-visible:ring-[#00C896]" />
            </div>
            <Button data-testid="save-budget-button" onClick={saveBudget} disabled={savingB || !bForm.term_label || !bForm.term_budget} className="w-full rounded-xl bg-[#00C896] hover:bg-[#00E5AC] text-[#04140D] font-semibold">
              {savingB ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save budget"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Item modal */}
      <Dialog open={itemOpen} onOpenChange={setItemOpen}>
        <DialogContent className="bg-[#14181D] border-white/10 text-white" data-testid="student-item-modal">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit expense" : "Add study expense"}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-slate-300 text-sm">What's it for?</Label>
              <Input data-testid="item-title-input" value={iForm.title} onChange={(e) => setIForm({ ...iForm, title: e.target.value })} placeholder="Statistics textbook" className="mt-1.5 bg-[#0D0D0D] border-white/10 text-white focus-visible:ring-[#00C896]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300 text-sm">Category</Label>
                <Select value={iForm.category} onValueChange={(v) => setIForm({ ...iForm, category: v })}>
                  <SelectTrigger data-testid="item-category-select" className="mt-1.5 bg-[#0D0D0D] border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#14181D] border-white/10 text-white">
                    {(data.categories || []).map((c) => <SelectItem key={c} value={c} className="focus:bg-[#00C896]/20">{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300 text-sm">Amount ({curSymbol()})</Label>
                <Input data-testid="item-amount-input" type="number" min="0" step="0.01" value={iForm.amount} onChange={(e) => setIForm({ ...iForm, amount: e.target.value })} placeholder="45" className="mt-1.5 bg-[#0D0D0D] border-white/10 text-white focus-visible:ring-[#00C896]" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300 text-sm">Due date</Label>
                <Input data-testid="item-due-input" type="date" value={iForm.due_date} onChange={(e) => setIForm({ ...iForm, due_date: e.target.value })} className="mt-1.5 bg-[#0D0D0D] border-white/10 text-white focus-visible:ring-[#00C896] [color-scheme:dark]" />
              </div>
              <div>
                <Label className="text-slate-300 text-sm">Status</Label>
                <Select value={iForm.status} onValueChange={(v) => setIForm({ ...iForm, status: v })}>
                  <SelectTrigger data-testid="item-status-select" className="mt-1.5 bg-[#0D0D0D] border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#14181D] border-white/10 text-white">
                    <SelectItem value="planned" className="focus:bg-[#00C896]/20">Planned</SelectItem>
                    <SelectItem value="paid" className="focus:bg-[#00C896]/20">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-slate-300 text-sm">Notes (optional)</Label>
              <Input data-testid="item-notes-input" value={iForm.notes} onChange={(e) => setIForm({ ...iForm, notes: e.target.value })} placeholder="Second-hand from library shop" className="mt-1.5 bg-[#0D0D0D] border-white/10 text-white focus-visible:ring-[#00C896]" />
            </div>
            <Button data-testid="save-item-button" onClick={saveItem} disabled={savingI || !iForm.title || !iForm.amount} className="w-full rounded-xl bg-[#00C896] hover:bg-[#00E5AC] text-[#04140D] font-semibold">
              {savingI ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save changes" : "Add expense"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
